import React, {useEffect, useState} from "react";
import {Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Stack, TextField, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {AmiRepository} from "../../../repositories/AmiRepository";
import {VariablesEditor} from "../../project/dialplan/components/VariablesEditor";
import {DEFAULT_CONTEXT, DEFAULT_TRUNK, ExtensionAdvancedSettings} from "../../project/dialplan/components/ExtensionAdvancedSettings";
import {getTarget, SubProjectFields, toVariableRows, toVariables} from "../../project/dialplan/components/SubProjectFields";

const sectionTitleSx = {fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", color: "#8A8AA0", mb: 1};
const buttonSx = (dark) => ({
  height: 38, px: 2, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600,
  ...(dark
    ? {color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"}, "&.Mui-disabled": {color: "#fff", bgcolor: "#C9CAD8"}}
    : {color: "#23264D", border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"}}),
});

// Edits an extension: details, the sub project / version it runs, trunk / context, variables and, while it is
// disabled, where its calls are redirected. projectId: the registry project.
export const ExtensionEditDialog = ({extension, projectId, subProjects, onClose, onSaved}) => {
  const open = Boolean(extension);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [trunk, setTrunk] = useState(DEFAULT_TRUNK);
  const [context, setContext] = useState(DEFAULT_CONTEXT);
  const [varRows, setVarRows] = useState([]);
  const [target, setTarget] = useState(null);
  const [redirectContext, setRedirectContext] = useState("");
  const [redirectExten, setRedirectExten] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingRedirect, setSavingRedirect] = useState(false);

  useEffect(() => {
    if (!extension) return;
    setName(extension.name || "");
    setDescription(extension.description || "");
    setTrunk(extension.trunk || DEFAULT_TRUNK);
    setContext(extension.context || DEFAULT_CONTEXT);
    setVarRows(toVariableRows(extension.variables));
    setTarget(getTarget(extension, subProjects));
    setRedirectContext(extension.redirect_context || "");
    setRedirectExten(extension.redirect_exten || "");
  }, [extension, subProjects]);

  const save = () => {
    setSaving(true);
    // owner and flow follow the selected sub project; without one the stored owner is kept
    const subProject = subProjects.find(item => item.id === target.subProjectId);
    AmiRepository.registerExtension(projectId, {
      extension: extension.extension,
      sub_project_id: subProject ? String(subProject.id) : undefined,
      flow_uid: subProject?.flowUid || extension.flow_uid,
      name: name.trim() || undefined,
      description,
      trunk: trunk.trim() || DEFAULT_TRUNK,
      context: context.trim() || DEFAULT_CONTEXT,
      variables: toVariables(varRows, target, subProjects, extension.variables, projectId),
    })
      .then((res) => {
        toast.success(`Extension ${extension.extension} updated`);
        if (res?.data?.conf_regenerated === false) toast.error(res?.data?.conf_error || "Configuration file failed to regenerate");
        onSaved({...extension, ...res?.data});
        onClose();
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to update extension"))
      .finally(() => setSaving(false));
  };

  const saveRedirect = () => {
    if (!redirectContext.trim() || !redirectExten.trim()) {
      toast.error("Both redirect context and extension are required");
      return;
    }
    setSavingRedirect(true);
    AmiRepository.redirectExtension(projectId, extension.extension, {context: redirectContext.trim(), exten: redirectExten.trim()})
      .then((res) => {
        toast.success(`Redirect target set for ${extension.extension}`);
        if (res?.data?.astdb_synced === false) toast.warning("Saved, but the live sync to Asterisk failed");
        onSaved({...extension, ...res?.data});
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to set redirect target"))
      .finally(() => setSavingRedirect(false));
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth={"sm"}
            PaperProps={{sx: {borderRadius: "20px", boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)"}}}>
      {extension && target && (
        <>
          <Stack direction={"row"} alignItems={"flex-start"} justifyContent={"space-between"} sx={{px: 3, pt: 2.5, pb: 1}}>
            <Box>
              <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>EDIT EXTENSION</Typography>
              <Typography sx={{fontSize: 22, fontWeight: 700, color: "#23264D", fontFamily: "monospace"}}>{extension.extension}</Typography>
              {extension.flow_uid && (
                <Typography sx={{fontSize: 11, color: "#8A8AA0", fontFamily: "monospace"}}>flow {extension.flow_uid}</Typography>
              )}
            </Box>
            <IconButton onClick={onClose} sx={{borderRadius: "50% !important", color: "#8A8AA0"}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <DialogContent sx={{px: 3, display: "flex", flexDirection: "column", gap: 2.5}}>
            <Box>
              <Typography sx={sectionTitleSx}>DETAILS</Typography>
              <Stack gap={1.5}>
                <TextField label={"Name"} size={"small"} fullWidth value={name} onChange={(e) => setName(e.target.value)}
                           placeholder={"e.g. Support bot"}/>
                <TextField label={"Description"} size={"small"} fullWidth multiline maxRows={3} value={description}
                           onChange={(e) => setDescription(e.target.value)}/>
              </Stack>
            </Box>

            <Box>
              <Typography sx={sectionTitleSx}>RUNS</Typography>
              <SubProjectFields subProjects={subProjects} target={target} onChange={setTarget}/>
            </Box>

            <ExtensionAdvancedSettings trunk={trunk} context={context} onTrunkChange={setTrunk} onContextChange={setContext}/>
            <VariablesEditor rows={varRows} onChange={setVarRows}/>

            {extension.enabled === false && (
              <Box sx={{p: 2, borderRadius: "14px", bgcolor: "#FDF8EE", border: "1px solid #F5E6C8"}}>
                <Typography sx={{...sectionTitleSx, color: "#A86A0C"}}>REDIRECT WHILE DISABLED</Typography>
                <Stack direction={{xs: "column", sm: "row"}} gap={1}>
                  <TextField size={"small"} label={"Context"} placeholder={"e.g. audiosocket-transfer"} sx={{flex: 1, bgcolor: "#fff"}}
                             value={redirectContext} onChange={(e) => setRedirectContext(e.target.value)}/>
                  <TextField size={"small"} label={"Extension"} placeholder={"e.g. 963"} sx={{flex: 1, bgcolor: "#fff"}}
                             value={redirectExten} onChange={(e) => setRedirectExten(e.target.value)}/>
                  <Button sx={buttonSx(false)} disabled={savingRedirect} onClick={saveRedirect}
                          startIcon={savingRedirect ? <CircularProgress size={14}/> : null}>
                    Save redirect
                  </Button>
                </Stack>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{px: 3, pb: 2.5, pt: 1, gap: 1}}>
            <Button sx={buttonSx(false)} onClick={onClose} disabled={saving}>Cancel</Button>
            <Button sx={buttonSx(true)} onClick={save} disabled={saving}
                    startIcon={saving ? <CircularProgress size={14} sx={{color: "#fff"}}/> : null}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
